import React, { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Point } from '@/types/common';
import { ScaleData } from '@/types/floorPlanViewer';
import { RoomDimensions } from './RoomDimensions';

interface RoomPolygonProps {
  id?: string;
  points: Point[];
  name?: string;
  scale: number;
  opacity: number;
  selected: boolean;
  onSelect: (e?: React.MouseEvent) => void;
  onPointDrag: (index: number, newPoint: Point) => void;
  onAddPoint: (index: number, newPoint: Point) => void;
  onRemovePoint?: (index: number) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  isDragging?: boolean;
  showLabel?: boolean;
  hasUnsavedChanges?: boolean;
  scaleData?: ScaleData | null;
  showDimensions?: boolean;
  showArea?: boolean;
  fillColor?: string;
  strokeColor?: string;
  segmentationMask?: string; // Base64 PNG mask data
  renderMode?: 'polygon' | 'mask' | 'both'; // How to render the room
  maskQuality?: 'high' | 'medium' | 'fallback' | null; // Quality of mask-derived polygon
}

export function RoomPolygon({
  id,
  points,
  name,
  scale,
  opacity = 0.5,
  selected,
  onSelect,
  onPointDrag,
  onAddPoint,
  onRemovePoint,
  onMouseDown,
  onDoubleClick,
  isDragging = false,
  showLabel = true,
  hasUnsavedChanges = false,
  scaleData = null,
  showDimensions = false,
  showArea = false,
  fillColor = '#3B82F6',
  strokeColor = '#3B82F6',
  segmentationMask,
  renderMode = 'polygon',
  maskQuality = null,
}: RoomPolygonProps) {
  const [isVertexDragging, setIsVertexDragging] = useState(false);
  const [draggingVertexIndex, setDraggingVertexIndex] = useState<number | null>(null);
  const [longPressVertexIndex, setLongPressVertexIndex] = useState<number | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [longPressProgress, setLongPressProgress] = useState(0);

  // Use refs to track state in event listeners
  const isVertexDraggingRef = useRef(false);
  const draggingVertexIndexRef = useRef<number | null>(null);
  const svgElementRef = useRef<SVGSVGElement | null>(null);
  const [cursorOffset, setCursorOffset] = useState<Point | null>(null);
  const cursorOffsetRef = useRef<Point | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchMoveHandlerRef = useRef<((e: TouchEvent) => void) | null>(null);
  const vertexPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const vertexDeletedRef = useRef(false); // Track if vertex was deleted to prevent further actions

  // Reference for mask canvas if needed
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [maskImage, setMaskImage] = useState<HTMLImageElement | null>(null);

  // Convert points to SVG path
  const pathData =
    points.length > 0
      ? `M ${points[0].x} ${points[0].y} ${points
          .slice(1)
          .map(p => `L ${p.x} ${p.y}`)
          .join(' ')} Z`
      : '';

  // Load mask image if available and render mode includes mask
  useEffect(() => {
    if (!segmentationMask || segmentationMask === '[]' || renderMode === 'polygon') {
      setMaskImage(null);
      return;
    }

    const img = new Image();
    img.onload = () => setMaskImage(img);
    img.onerror = () => setMaskImage(null);

    const maskData = segmentationMask.startsWith('data:')
      ? segmentationMask
      : `data:image/png;base64,${segmentationMask}`;

    img.src = maskData;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [segmentationMask, renderMode]);

  // Calculate optimal label position using pole of inaccessibility algorithm
  const calculateLabelPosition = () => {
    if (points.length === 0) return { x: 0, y: 0 };

    // Get bounding box
    const xValues = points.map(p => p.x);
    const yValues = points.map(p => p.y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    // Check if a point is inside the polygon
    const isPointInPolygon = (point: Point, polygon: Point[]) => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x,
          yi = polygon[i].y;
        const xj = polygon[j].x,
          yj = polygon[j].y;

        const intersect =
          yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    };

    // Calculate distance from a point to the nearest edge
    const pointToPolygonDistance = (point: Point, polygon: Point[]) => {
      let minDistance = Infinity;

      for (let i = 0; i < polygon.length; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % polygon.length];

        // Distance from point to line segment
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const lengthSquared = dx * dx + dy * dy;

        if (lengthSquared === 0) {
          // p1 and p2 are the same point
          const dist = Math.sqrt((point.x - p1.x) ** 2 + (point.y - p1.y) ** 2);
          minDistance = Math.min(minDistance, dist);
        } else {
          // Calculate parameter t that represents position along the line segment
          const t = Math.max(
            0,
            Math.min(1, ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared),
          );

          // Find the closest point on the line segment
          const closestX = p1.x + t * dx;
          const closestY = p1.y + t * dy;

          // Calculate distance
          const dist = Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
          minDistance = Math.min(minDistance, dist);
        }
      }

      return minDistance;
    };

    // Grid-based search for the best position
    const gridSize = Math.min((maxX - minX) / 10, (maxY - minY) / 10); // Adaptive grid size
    let bestPoint = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
    let maxDistance = 0;

    // First pass: coarse grid
    for (let x = minX; x <= maxX; x += gridSize) {
      for (let y = minY; y <= maxY; y += gridSize) {
        const point = { x, y };
        if (isPointInPolygon(point, points)) {
          const distance = pointToPolygonDistance(point, points);
          if (distance > maxDistance) {
            maxDistance = distance;
            bestPoint = point;
          }
        }
      }
    }

    // Second pass: refine around the best point
    const fineGridSize = gridSize / 4;
    const searchRadius = gridSize * 2;

    for (let x = bestPoint.x - searchRadius; x <= bestPoint.x + searchRadius; x += fineGridSize) {
      for (let y = bestPoint.y - searchRadius; y <= bestPoint.y + searchRadius; y += fineGridSize) {
        const point = { x, y };
        if (isPointInPolygon(point, points)) {
          const distance = pointToPolygonDistance(point, points);
          if (distance > maxDistance) {
            maxDistance = distance;
            bestPoint = point;
          }
        }
      }
    }

    // Fallback to centroid if no good point found
    if (maxDistance === 0) {
      const centroid = {
        x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
        y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
      };

      if (isPointInPolygon(centroid, points)) {
        return centroid;
      }
    }

    return bestPoint;
  };

  // Memoize label position calculation (expensive for complex polygons)
  const labelPosition = useMemo(() => calculateLabelPosition(), [points]);

  // Determine if we should show interactive elements based on complexity and zoom
  // For very complex polygons (>50 points), only show vertices when zoomed in (scale < 1)
  const shouldShowVertices = useMemo(() => {
    if (points.length <= 50) return true;
    if (points.length <= 100) return scale < 1.5;
    return scale < 1; // Very complex polygons only show vertices when well zoomed in
  }, [points.length, scale]);

  // Find SVG element on mount
  useEffect(() => {
    // Find the parent SVG element that contains this room polygon
    const findSvgElement = () => {
      // Try to find the SVG that's a parent of any room polygon
      const roomElement = document.querySelector('[data-room-id]');
      if (roomElement) {
        const svg = roomElement.closest('svg');
        if (svg) {
          svgElementRef.current = svg as SVGSVGElement;
        }
      }
    };

    findSvgElement();
  }, []);

  // Global vertex movement handler for tracking dragging outside the component
  const handleGlobalVertexMove = (e: MouseEvent | TouchEvent) => {
    // Don't process if vertex was deleted
    if (vertexDeletedRef.current) {
      return;
    }

    // CRITICAL: Immediately prevent default on touch events to stop browser scrolling
    if ('touches' in e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear long press timer on movement
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // Check for multi-touch and bail out if detected
    if ('touches' in e && e.touches.length > 1) {
      // Multi-touch detected, stop vertex dragging
      setIsVertexDragging(false);
      setDraggingVertexIndex(null);
      setLongPressVertexIndex(null);
      isVertexDraggingRef.current = false;
      draggingVertexIndexRef.current = null;
      return;
    }

    if (
      isVertexDraggingRef.current &&
      draggingVertexIndexRef.current !== null &&
      svgElementRef.current &&
      cursorOffsetRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();

      // Get coordinates from mouse or touch event
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      // Convert screen coordinates to SVG coordinates
      // Since we know our SVG has a specific viewBox offset, we need to account for it
      const rect = svgElementRef.current.getBoundingClientRect();
      const pt = svgElementRef.current.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;

      // Transform the point from screen space to SVG space
      const svgPt = pt.matrixTransform(svgElementRef.current.getScreenCTM()?.inverse());

      // Apply the cursor offset to maintain relative position
      const newPoint = {
        x: svgPt.x - cursorOffsetRef.current.x,
        y: svgPt.y - cursorOffsetRef.current.y,
      };

      // Call the drag handler with the index and new position
      onPointDrag(draggingVertexIndexRef.current, newPoint);
    }
  };

  // Global mouse up handler to clean up after dragging
  const handleGlobalMouseUp = (e: MouseEvent | TouchEvent) => {
    // Don't process if vertex was deleted
    if (vertexDeletedRef.current) {
      return;
    }

    // CRITICAL: Immediately prevent default on touch events
    if ('touches' in e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (isVertexDraggingRef.current || draggingVertexIndexRef.current !== null) {
      e.preventDefault();
      e.stopPropagation();

      setIsVertexDragging(false);
      setDraggingVertexIndex(null);
      setLongPressVertexIndex(null);
      isVertexDraggingRef.current = false;
      draggingVertexIndexRef.current = null;
      cursorOffsetRef.current = null;
      vertexPressStartRef.current = null;

      // Remove the vertex-specific event listeners (must match the addEventListener options)
      document.removeEventListener('mousemove', handleGlobalVertexMove, { capture: true } as any);
      document.removeEventListener('mouseup', handleGlobalMouseUp, { capture: true } as any);
      document.removeEventListener(
        'touchmove',
        handleGlobalVertexMove as any,
        { capture: true } as any,
      );
      document.removeEventListener(
        'touchend',
        handleGlobalMouseUp as any,
        { capture: true } as any,
      );
    }
  };

  // Handle vertex right-click for removal
  const handleVertexRightClick = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only allow removal if there are more than 3 points (minimum for a polygon)
    if (points.length > 3 && onRemovePoint) {
      onRemovePoint(index);
    }
  };

  // Handle vertex drag start
  const handleVertexMouseDown = (index: number, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check for multi-touch and bail out if detected
    if ('touches' in e && e.touches.length > 1) {
      return; // Don't handle vertex dragging during multi-touch
    }

    // Get coordinates from mouse or touch event
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Store press start position for movement detection
    vertexPressStartRef.current = { x: clientX, y: clientY };

    // Find SVG element if not already found
    if (!svgElementRef.current) {
      svgElementRef.current = e.currentTarget.closest('svg') as SVGSVGElement;
    }

    // Show visual feedback after 200ms
    setTimeout(() => {
      if (isVertexDraggingRef.current && draggingVertexIndexRef.current === index) {
        setLongPressVertexIndex(index);
      }
    }, 200);

    // Start long-press timer for deletion (600ms)
    longPressTimerRef.current = setTimeout(() => {
      // Check if we haven't moved much (to distinguish from drag)
      if (vertexPressStartRef.current) {
        const currentX = clientX;
        const currentY = clientY;
        const distance = Math.sqrt(
          Math.pow(currentX - vertexPressStartRef.current.x, 2) +
            Math.pow(currentY - vertexPressStartRef.current.y, 2),
        );

        if (distance < 5 && onRemovePoint && points.length > 3) {
          // Set flag to indicate vertex was deleted
          vertexDeletedRef.current = true;

          // CRITICAL: Remove event listeners BEFORE clearing state to prevent orphaned handlers
          document.removeEventListener('mousemove', handleGlobalVertexMove, {
            capture: true,
          } as any);
          document.removeEventListener('mouseup', handleGlobalMouseUp, { capture: true } as any);
          document.removeEventListener(
            'touchmove',
            handleGlobalVertexMove as any,
            { capture: true } as any,
          );
          document.removeEventListener(
            'touchend',
            handleGlobalMouseUp as any,
            { capture: true } as any,
          );

          // Clear all states
          setLongPressVertexIndex(null);
          setIsVertexDragging(false);
          setDraggingVertexIndex(null);
          isVertexDraggingRef.current = false;
          draggingVertexIndexRef.current = null;
          cursorOffsetRef.current = null;
          vertexPressStartRef.current = null;

          // Clear the timer
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
          }

          // Trigger vertex removal
          onRemovePoint(index);

          // Reset the deleted flag after a short delay
          setTimeout(() => {
            vertexDeletedRef.current = false;
          }, 100);

          // Return early to prevent further setup
          return;
        }
      }
    }, 600);

    // Save the vertex index
    setDraggingVertexIndex(index);
    draggingVertexIndexRef.current = index;

    // Calculate the offset between cursor and vertex center
    if (svgElementRef.current) {
      const pt = svgElementRef.current.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const svgPt = pt.matrixTransform(svgElementRef.current.getScreenCTM()?.inverse());

      const offset = {
        x: svgPt.x - points[index].x,
        y: svgPt.y - points[index].y,
      };
      setCursorOffset(offset);
      cursorOffsetRef.current = offset;
    }

    // Set dragging state
    setIsVertexDragging(true);
    isVertexDraggingRef.current = true;

    // Add global event listeners with passive: false to allow preventDefault
    document.addEventListener('mousemove', handleGlobalVertexMove, {
      capture: true,
      passive: false,
    });
    document.addEventListener('mouseup', handleGlobalMouseUp, { capture: true, passive: false });
    document.addEventListener('touchmove', handleGlobalVertexMove as any, {
      capture: true,
      passive: false,
    });
    document.addEventListener('touchend', handleGlobalMouseUp as any, {
      capture: true,
      passive: false,
    });

    // Prevent event bubbling to stop room dragging
    return false;
  };

  // Handle polygon mouse down
  const handlePolygonMouseDown = (e: React.MouseEvent) => {
    // Don't handle if we're vertex dragging
    if (
      draggingVertexIndex !== null ||
      draggingVertexIndexRef.current !== null ||
      isVertexDragging ||
      isVertexDraggingRef.current
    ) {
      return;
    }

    e.stopPropagation();

    // Always call onSelect on click with the event
    onSelect(e);

    // If there's an onMouseDown handler (for room dragging), call it
    if (onMouseDown) {
      onMouseDown(e);
    }
  };

  // Handle polygon touch start for tablet/mobile with long-press detection
  const handlePolygonTouchStart = (e: React.TouchEvent) => {
    // Don't handle if we're vertex dragging
    if (
      draggingVertexIndex !== null ||
      draggingVertexIndexRef.current !== null ||
      isVertexDragging ||
      isVertexDraggingRef.current
    ) {
      return;
    }

    // IMPORTANT: Detect multi-touch (pinch-to-zoom) and bail out
    if (e.touches.length > 1) {
      // Clear any existing long-press timers using refs
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (touchMoveHandlerRef.current) {
        document.removeEventListener('touchmove', touchMoveHandlerRef.current);
        touchMoveHandlerRef.current = null;
      }
      setIsLongPressing(false);
      setLongPressProgress(0);
      return; // Don't handle room interactions during multi-touch
    }

    // Stop propagation to prevent other handlers
    // Note: We don't call preventDefault() here because React touch events are passive by default
    e.stopPropagation();

    // Convert touch event to have similar properties as mouse event for compatibility
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    const mockMouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    } as React.MouseEvent;

    // Call onSelect for selection
    onSelect(mockMouseEvent);

    // Start long-press detection for room dragging
    setIsLongPressing(true);
    setLongPressProgress(0);

    let touchMoveHandler: ((e: TouchEvent) => void) | null = null;

    // Add touch move handler to detect movement and cancel long-press
    touchMoveHandler = (moveEvent: TouchEvent) => {
      // Always prevent default to stop browser scrolling
      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      if (moveEvent.touches.length > 1) {
        // Multi-touch detected, cancel long-press
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setIsLongPressing(false);
        setLongPressProgress(0);
        if (touchMoveHandlerRef.current) {
          document.removeEventListener('touchmove', touchMoveHandlerRef.current);
          touchMoveHandlerRef.current = null;
        }
        return;
      }

      const touch = moveEvent.touches[0];
      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If finger moves more than 5px, cancel long-press (require stationary hold)
      if (distance > 5) {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setIsLongPressing(false);
        setLongPressProgress(0);
        if (touchMoveHandlerRef.current) {
          document.removeEventListener('touchmove', touchMoveHandlerRef.current);
          touchMoveHandlerRef.current = null;
        }
      }
    };

    document.addEventListener('touchmove', touchMoveHandler, { passive: false });

    const longPressTimer = setTimeout(() => {
      // After 500ms of stationary hold, enable dragging
      if (onMouseDown) {
        setIsLongPressing(false);
        setLongPressProgress(100);
        // Trigger haptic feedback if available
        if ('vibrate' in navigator) {
          navigator.vibrate(10);
        }
        // Remove the movement detector since we're now dragging
        if (touchMoveHandler) {
          document.removeEventListener('touchmove', touchMoveHandler);
        }
        onMouseDown(e as any);
      }
    }, 500);

    // Animate progress indicator
    const progressInterval = setInterval(() => {
      setLongPressProgress(prev => Math.min(prev + 20, 100));
    }, 100);

    // Store timer refs and handler to clear on touch end
    longPressTimerRef.current = longPressTimer;
    progressIntervalRef.current = progressInterval;
    touchMoveHandlerRef.current = touchMoveHandler;
  };

  // Handle touch end to clear long-press timers
  const handlePolygonTouchEnd = (e: React.TouchEvent) => {
    // Clear long-press timers
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    // Clean up touchMoveHandler
    if (touchMoveHandlerRef.current) {
      document.removeEventListener('touchmove', touchMoveHandlerRef.current);
      touchMoveHandlerRef.current = null;
    }

    setIsLongPressing(false);
    setLongPressProgress(0);

    // Handle double tap for selection
    const now = Date.now();
    const lastTap = target.lastTap || 0;
    if (now - lastTap < 300) {
      // Double tap detected
      handleDoubleClick(e as any);
    }
    target.lastTap = now;
  };

  // Calculate midpoints for adding new points (memoized for performance)
  const midpoints = useMemo(
    () =>
      points.map((point, index) => {
        const nextIndex = (index + 1) % points.length;
        const nextPoint = points[nextIndex];
        return {
          x: (point.x + nextPoint.x) / 2,
          y: (point.y + nextPoint.y) / 2,
          index,
        };
      }),
    [points],
  );

  // Handle adding a new point when midpoint is clicked/tapped
  const handleMidpointClick = (index: number, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const midpoint = midpoints[index];
    onAddPoint(index + 1, { x: midpoint.x, y: midpoint.y });
  };

  // Custom click handler
  const handleClick = (e: React.MouseEvent) => {
    // Prevent double selection since we handle it in mouseDown
    e.stopPropagation();
  };

  // Handle double click
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDoubleClick) {
      onDoubleClick();
    }
  };

  // Add global event handlers for cleanup
  useEffect(() => {
    // Cleanup function for when component unmounts
    return () => {
      // Clear any pending timers
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      // Remove touch move handler if it exists
      if (touchMoveHandlerRef.current) {
        document.removeEventListener('touchmove', touchMoveHandlerRef.current);
      }
      // Remove all event listeners (must match the addEventListener options)
      document.removeEventListener('mousemove', handleGlobalVertexMove, { capture: true } as any);
      document.removeEventListener('mouseup', handleGlobalMouseUp, { capture: true } as any);
      document.removeEventListener(
        'touchmove',
        handleGlobalVertexMove as any,
        { capture: true } as any,
      );
      document.removeEventListener(
        'touchend',
        handleGlobalMouseUp as any,
        { capture: true } as any,
      );
    };
  }, []);

  return (
    <g
      data-room-id={id} // Add data attribute for drag handler to find
      onMouseDown={handlePolygonMouseDown}
      onTouchStart={handlePolygonTouchStart}
      onTouchEnd={handlePolygonTouchEnd}
      onDoubleClick={handleDoubleClick}
      style={{
        cursor: isDragging ? 'grabbing' : selected ? 'grab' : 'pointer',
        pointerEvents: isVertexDragging ? 'none' : 'all',
        touchAction: 'none', // Prevent browser touch handling on room polygon
      }}
      onClick={handleClick}
      className={isDragging ? 'room-dragging' : ''}
    >
      {/* Room dimensions - render underneath polygon */}
      {showDimensions && scaleData && !isDragging && (
        <RoomDimensions
          points={points}
          scale={scale}
          scaleData={scaleData}
          showDimensions={showDimensions}
          showArea={showArea && showDimensions} // Only show area when dimensions are on
          isSelected={selected}
          opacity={0.8}
          offsetDistance={20}
          roomName={undefined} // Don't pass room name to dimensions
          showRoomName={false}
          minimalMode={false}
        />
      )}

      {/* Room polygon */}
      <path
        d={pathData}
        fill={selected ? `${fillColor}33` : fillColor}
        fillOpacity={selected ? 0.3 : opacity}
        stroke={hasUnsavedChanges ? 'rgb(255, 140, 0)' : selected ? strokeColor : strokeColor}
        strokeOpacity={hasUnsavedChanges ? 1 : selected ? 1 : 0.8}
        strokeWidth={hasUnsavedChanges ? 3 / scale : selected ? 2 / scale : 1 / scale}
        strokeDasharray={hasUnsavedChanges ? `${5 / scale} ${3 / scale}` : undefined}
        style={{
          pointerEvents: isVertexDragging || draggingVertexIndex !== null ? 'none' : 'all',
          transition: isDragging ? 'none' : 'fill 0.2s, stroke 0.2s',
          touchAction: 'none', // Prevent browser touch handling on path element
        }}
        className={cn(selected ? 'z-10' : 'z-0')}
      />

      {/* Room label - offset vertically if area is also shown with dimensions */}
      {name && showLabel && (
        <g pointerEvents="none">
          {/* Adjust Y position based on whether area is shown WITH dimensions */}
          <text
            x={labelPosition.x}
            y={labelPosition.y - (showArea && showDimensions && scaleData ? 10 / scale : 0)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={
              hasUnsavedChanges
                ? 'rgb(255, 140, 0)'
                : selected
                  ? 'rgb(0, 80, 255)'
                  : 'rgb(0, 50, 150)'
            }
            fontSize={12 / scale}
            fontWeight={selected ? 'bold' : 'normal'}
            style={{
              filter: 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.9))', // Stronger shadow for better readability
            }}
          >
            {name.length > 15 ? name.substring(0, 12) + '...' : name}
            {hasUnsavedChanges ? '*' : ''}
          </text>

          {/* Quality indicator - small circle next to label */}
          {maskQuality && (
            <circle
              cx={labelPosition.x + ((name.length > 15 ? 12 : name.length) * 6) / scale + 8 / scale}
              cy={labelPosition.y - (showArea && showDimensions && scaleData ? 10 / scale : 0)}
              r={3 / scale}
              fill={
                maskQuality === 'high'
                  ? 'rgb(34, 197, 94)' // green
                  : maskQuality === 'medium'
                    ? 'rgb(234, 179, 8)' // yellow
                    : 'rgb(156, 163, 175)' // gray
              }
              stroke="white"
              strokeWidth={0.5 / scale}
              style={{
                filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.3))',
              }}
            />
          )}
        </g>
      )}

      {/* Long-press progress indicator */}
      {isLongPressing && selected && (
        <g pointerEvents="none">
          <circle
            cx={labelPosition.x}
            cy={labelPosition.y}
            r={20 / scale}
            fill="none"
            stroke="rgba(0, 120, 255, 0.2)"
            strokeWidth={3 / scale}
          />
          <circle
            cx={labelPosition.x}
            cy={labelPosition.y}
            r={20 / scale}
            fill="none"
            stroke="rgba(0, 120, 255, 0.8)"
            strokeWidth={3 / scale}
            strokeDasharray={`${(125.6 * longPressProgress) / 100 / scale} ${125.6 / scale}`}
            strokeDashoffset={0}
            transform={`rotate(-90 ${labelPosition.x} ${labelPosition.y})`}
            style={{
              transition: 'stroke-dasharray 0.1s linear',
            }}
          />
        </g>
      )}

      {/* Draggable vertices (only when selected and not being dragged) */}
      {/* For complex polygons, only show vertices when zoomed in for better performance */}
      {selected &&
        !isDragging &&
        shouldShowVertices &&
        points.map((point, index) => (
          <g key={`vertex-${index}`}>
            {/* Invisible larger touch target for better mobile UX */}
            <circle
              cx={point.x}
              cy={point.y}
              r={12 / scale} // Larger hit area for touch
              fill="transparent"
              stroke="none"
              onMouseDown={e => handleVertexMouseDown(index, e)}
              onTouchStart={e => handleVertexMouseDown(index, e)}
              onContextMenu={e => handleVertexRightClick(index, e)}
              style={{
                cursor: 'move',
                pointerEvents: 'all',
                touchAction: 'none', // Prevent default touch behavior
                WebkitTouchCallout: 'none', // Prevent iOS callout menu
                WebkitUserSelect: 'none', // Prevent text selection on iOS
              }}
            />
            {/* Visible vertex */}
            <circle
              cx={point.x}
              cy={point.y}
              r={6 / scale}
              fill="white"
              stroke="rgb(0, 100, 255)"
              strokeWidth={2 / scale}
              pointerEvents="none" // Let the invisible circle handle events
            />
            {/* Visual hint for long-press to delete */}
            {longPressVertexIndex === index && (
              <circle
                cx={point.x}
                cy={point.y}
                r={8 / scale}
                fill="none"
                stroke="red"
                strokeWidth={2 / scale}
                strokeDasharray={`${2 / scale} ${2 / scale}`}
                pointerEvents="none"
                className="animate-pulse"
              />
            )}
          </g>
        ))}

      {/* Midpoints for adding new vertices (only when selected and not being dragged) */}
      {/* For complex polygons, only show midpoints when zoomed in for better performance */}
      {selected &&
        !isDragging &&
        shouldShowVertices &&
        midpoints.map((midpoint, index) => (
          <circle
            key={`midpoint-${index}`}
            cx={midpoint.x}
            cy={midpoint.y}
            r={5 / scale}
            fill="rgba(0, 255, 0, 0.3)"
            stroke="rgb(0, 200, 0)"
            strokeWidth={1 / scale}
            onClick={e => handleMidpointClick(index, e)}
            onTouchEnd={e => handleMidpointClick(index, e)}
            style={{
              cursor: 'pointer',
              touchAction: 'none', // Prevent default touch behavior
              WebkitTouchCallout: 'none', // Prevent iOS callout menu
              WebkitUserSelect: 'none', // Prevent text selection on iOS
            }}
          />
        ))}
    </g>
  );
}
